// Frontend RBAC map — mirror of backend/config/rbac.js.
//
// Keep in sync manually. When you add a resource or action here, add the
// same entry to the backend map (or the reverse). The two files are
// intentionally structurally identical so `diff` is a reliable audit.
//
// Only frontend code reads this file; PermissionGate + RequireRole call
// `hasPermission()` below. Backend authorization is authoritative — this
// mirror is for hiding UI and for pre-fetch redirect, not for security.

const SUPER_ADMINS  = ['admin', 'systemAdmin'];
// NOTE: 'org' is intentionally NOT in ORG_ADMINS. The LEGACY_TO_MODERN map
// aliases 'org' → 'claimsManager', so listing 'org' here would transitively
// grant claimsManager every ORG_ADMINS-gated resource (Team Management, rate
// tables, audit, integrity, DSAR, etc.). Legacy 'org' users are still
// served — expandRoles picks up the alias at check time.
const ORG_ADMINS    = [...SUPER_ADMINS, 'orgAdmin'];
// PLATFORM_ADMIN is the SaaS God only. See backend/config/rbac.js for
// the full rationale. Deliberately excludes systemAdmin so tenant admins
// don't see the Platform sidebar section or /platform/* routes.
const PLATFORM_ADMIN = ['admin'];
const CLAIMS_STAFF  = ['claimsManager', 'seniorAdjuster', 'adjuster', 'fieldAdjuster'];
const COMPLIANCE    = ['complianceOfficer'];
const UNDERWRITING  = ['underwriter'];
const EXTERNAL      = ['broker', 'policyholder', 'riskCarrier', 'externalContractor', 'claimantPortalUser'];

const ALL_INTERNAL     = [...ORG_ADMINS, ...CLAIMS_STAFF, ...COMPLIANCE, ...UNDERWRITING];
const CLAIMS_WRITE     = [...ORG_ADMINS, 'claimsManager', 'seniorAdjuster'];
const CLAIMS_ASSIGN    = [...ORG_ADMINS, 'claimsManager'];
const CLAIMS_ADJUSTER  = [...ORG_ADMINS, ...CLAIMS_STAFF];
// Underwriters are read-only across the platform (assessment role, not
// creation). Do NOT add 'underwriter' back to POLICY_WRITE / OFFER_WRITE
// without an explicit product decision — the sidebar + button gating
// derives from this list.
const POLICY_WRITE     = [...ORG_ADMINS, 'claimsManager', 'seniorAdjuster'];
const OFFER_WRITE      = [...ORG_ADMINS, 'claimsManager', 'seniorAdjuster'];
const AUDIT_READ       = [...ORG_ADMINS, ...COMPLIANCE];
const ADMIN_ONLY       = SUPER_ADMINS;

export const RBAC = {
  // Dashboard tile & Claims-Analytics deck are gated separately from the
  // claims data resource. Compliance Officer audits claims via /audit-logs,
  // not the dashboard, so complianceOfficer is excluded here even though
  // they have claims.read.
  dashboard: {
    read: [...ORG_ADMINS, ...CLAIMS_STAFF, ...UNDERWRITING],
  },
  // M-18..M-23: claimsManager and seniorAdjuster need /claims-analytics
  // — the screen exposes broker / canton / specialisation / reserve
  // breakdowns that are core to their day job. Anjani (sdumont =
  // claimsManager) marked the whole M section Blocked with "Claims
  // analytics or claims dashboard is missing" because the sidebar hid
  // the link. Widening here + in the backend mirror. Adjuster/
  // fieldAdjuster stay excluded — they see their own caseload on the
  // dashboard, not the tenant-wide analytics view.
  analytics: {
    read: [...ORG_ADMINS, ...UNDERWRITING, 'claimsManager', 'seniorAdjuster'],
  },
  claims: {
    read:      [...ALL_INTERNAL, ...EXTERNAL],
    create:    CLAIMS_WRITE,
    update:    CLAIMS_WRITE,
    assign:    CLAIMS_ASSIGN,
    delete:    ORG_ADMINS,
    // Historical load. Accepts dates and terminal states the normal create
    // path deliberately refuses, so it is tenant-admin only — narrower than
    // `create` (which CLAIMS_WRITE holds) precisely because backdating a
    // record is not an everyday operation.
    import:    ORG_ADMINS,
    // note: adjuster + fieldAdjuster are read-only per T-10.11. Kept the
    // CLAIMS_ADJUSTER group above for documents.classify only.
    note:      CLAIMS_WRITE,
    reserve:   CLAIMS_WRITE,
    legalHold: [...ORG_ADMINS, ...COMPLIANCE],
    payment:   CLAIMS_WRITE,
  },
  clients: {
    // Compliance Officer audits client data via the audit log, not the
    // Clients list. 'broker' kept so pierre (broker portal) can see
    // my-clients portal nav item.
    read:   [...ORG_ADMINS, ...CLAIMS_STAFF, ...UNDERWRITING, 'broker'],
    create: [...ORG_ADMINS, 'claimsManager', 'seniorAdjuster'],
    update: [...ORG_ADMINS, 'claimsManager', 'seniorAdjuster'],
    delete: ORG_ADMINS,
  },
  policies: {
    // complianceOfficer: read-only for audit purposes.
    read:   [...ORG_ADMINS, ...CLAIMS_STAFF, ...UNDERWRITING, ...COMPLIANCE, 'broker'],
    create: POLICY_WRITE,
    update: POLICY_WRITE,
    delete: ORG_ADMINS,
  },
  offers: {
    // complianceOfficer: read-only for audit purposes.
    read:   [...ORG_ADMINS, ...CLAIMS_STAFF, ...UNDERWRITING, ...COMPLIANCE, 'broker'],
    create: OFFER_WRITE,
    update: OFFER_WRITE,
    // Mirrors backend config/rbac.js. Deleting a quote is a SOFT delete and
    // the real test is per-record — systemAdmin, or the person who keyed it
    // — which a role list cannot express. This gate only decides who is
    // shown the control at all; the server decides who may use it, and the
    // screen asks the same question locally before rendering the button.
    delete: OFFER_WRITE,
  },
  rateTables: {
    // Anyone who can create an offer needs to READ rate tables — the offer
    // form fetches current rates to price the quote (T-8.5). Write stays
    // limited to admins + underwriters.
    read:   [...ORG_ADMINS, ...UNDERWRITING, 'claimsManager', 'seniorAdjuster'],
    write:  [...ORG_ADMINS, ...UNDERWRITING],
    delete: ORG_ADMINS,
  },
  bordereaux: {
    read:     [...ORG_ADMINS, ...COMPLIANCE],
    generate: [...ORG_ADMINS, 'claimsManager', 'seniorAdjuster'],
    send:     ORG_ADMINS,
    validate: [...ORG_ADMINS, ...COMPLIANCE],
    delete:   ORG_ADMINS,
  },
  documents: {
    // Enterprise-standard: every workflow role uploads/links documents as
    // part of their normal work. Delete stays admin-only.
    read:     [...ORG_ADMINS, ...CLAIMS_STAFF, ...UNDERWRITING, ...EXTERNAL],
    upload:   [...ORG_ADMINS, ...CLAIMS_STAFF],
    update:   [...ORG_ADMINS, ...CLAIMS_STAFF],
    delete:   ORG_ADMINS,
    classify: ALL_INTERNAL,
    link:     [...ORG_ADMINS, ...CLAIMS_STAFF],
  },
  brokers: {
    // Brokers page is Administration-scoped; claims staff & compliance
    // reach broker detail via broker chips on claims/policies rather than
    // the /brokers list. Sidebar link is admin/underwriting only.
    read:   [...ORG_ADMINS, ...UNDERWRITING],
    create: [...ORG_ADMINS, 'claimsManager'],
    update: [...ORG_ADMINS, 'claimsManager'],
    delete: ORG_ADMINS,
  },
  commissions: {
    // adjuster / fieldAdjuster do NOT see the Commissions sidebar link
    // (per T-9.15). claimsManager + seniorAdjuster were dropped in the
    // 2026-08 AROMED acceptance run (D-08): the tester expected only
    // admins, underwriting and brokers to see Commissions, matching
    // the Setup description of claimsManager as "manages claims,
    // assigns adjusters, approves reserves" (no commission scope).
    read:   [...ORG_ADMINS, ...UNDERWRITING, 'broker'],
    create: ORG_ADMINS,
    update: ORG_ADMINS,
    delete: ORG_ADMINS,
  },
  riskCarriers: {
    // Read widened to all internal staff: an adjuster handling a case needs
    // to see which carrier/provider is behind it, and the routes are now
    // authorization-gated (they previously had none, which let EXTERNAL
    // portal users read and write them). Administration stays with admins
    // and underwriting.
    //
    // EXTERNAL roles — broker, policyholder, riskCarrier, claimantPortalUser
    // — are deliberately in neither list. The `riskCarrier` role itself has
    // no read access because no external provider portal exists yet.
    read:  [...ALL_INTERNAL],
    write: [...ORG_ADMINS, ...UNDERWRITING],
  },
  riskSignals: {
    read:  [...ORG_ADMINS, ...UNDERWRITING],
    write: [...ORG_ADMINS, ...CLAIMS_STAFF],
  },
  users: {
    // Enterprise-standard: claimsManager can view the team roster to route
    // work. Writes stay admin-only. complianceOfficer audits via the log,
    // not the /users list — hence not added (previous N11 leak stays
    // closed).
    read:   [...ORG_ADMINS, 'claimsManager'],
    create: ORG_ADMINS,
    update: ORG_ADMINS,
    delete: ORG_ADMINS,
    invite: ORG_ADMINS,
  },
  orgs: {
    read:   [...ALL_INTERNAL, ...EXTERNAL],
    update: ORG_ADMINS,
    delete: SUPER_ADMINS,
  },
  audit: {
    // Enterprise-standard: every workflow role can read their own tenant's
    // audit trail. Verify (hash-chain check) stays compliance-tier.
    read:   [...AUDIT_READ, ...CLAIMS_STAFF],
    verify: AUDIT_READ,
  },
  integrity: {
    read:   AUDIT_READ,
    verify: AUDIT_READ,
    anchor: ADMIN_ONLY,
  },
  workflows: {
    read:   ORG_ADMINS,
    create: [...ORG_ADMINS, 'claimsManager'],
    update: [...ORG_ADMINS, 'claimsManager'],
    delete: ORG_ADMINS,
  },
  chat: {
    read:  [...ALL_INTERNAL, ...EXTERNAL],
    write: [...ALL_INTERNAL, ...EXTERNAL],
    admin: ORG_ADMINS,
  },
  video: {
    read:  [...ALL_INTERNAL, ...EXTERNAL],
    write: [...ALL_INTERNAL, ...EXTERNAL],
  },
  notifications: {
    read:  [...ALL_INTERNAL, ...EXTERNAL],
    write: ORG_ADMINS,
  },
  apiKeys: {
    read:   ORG_ADMINS,
    create: ORG_ADMINS,
    update: ORG_ADMINS,
    delete: ORG_ADMINS,
  },
  webhooks: {
    read:   ORG_ADMINS,
    create: ORG_ADMINS,
    update: ORG_ADMINS,
    delete: ORG_ADMINS,
  },
  dsar: {
    read:   [...ORG_ADMINS, ...COMPLIANCE],
    create: [...ORG_ADMINS, ...COMPLIANCE, 'policyholder'],
    update: [...ORG_ADMINS, ...COMPLIANCE],
  },
  aiGovernance: {
    read:  [...ORG_ADMINS, ...COMPLIANCE],
    write: [...ORG_ADMINS, ...COMPLIANCE],
  },
  dataResidency: {
    read:  ORG_ADMINS,
    write: ORG_ADMINS,
  },
  compliance: {
    read:  [...ORG_ADMINS, ...COMPLIANCE],
    write: [...ORG_ADMINS, ...COMPLIANCE],
  },
  // PLATFORM_ADMIN = ['admin'] — SaaS God only. Excludes systemAdmin
  // (tenant admin) so the Platform sidebar section + /platform/* routes
  // never leak cross-tenant data. Aliasing between admin↔systemAdmin was
  // removed from LEGACY_TO_MODERN below to make this gate airtight.
  platform: {
    read:  PLATFORM_ADMIN,
    write: PLATFORM_ADMIN,
  },
  invoices: {
    read:   ORG_ADMINS,
    create: ORG_ADMINS,
    update: ORG_ADMINS,
    delete: SUPER_ADMINS,
  },
  coupons: {
    read:  ORG_ADMINS,
    write: ADMIN_ONLY,
  },
  eSign: {
    read:   [...ALL_INTERNAL, ...EXTERNAL],
    write:  [...ORG_ADMINS, ...CLAIMS_STAFF, ...UNDERWRITING],
    // Reseal is admin-only recovery for seal_failed rows — see backend map.
    reseal: ORG_ADMINS,
  },
  tenantConfig: {
    read:  [...ALL_INTERNAL, ...EXTERNAL],
    write: ADMIN_ONLY,
  },

  // Reinsurance module — mirror of backend/config/rbac.js.
  reinsurance: {
    read:     [...ORG_ADMINS, ...UNDERWRITING, 'claimsManager', 'seniorAdjuster', 'broker'],
    create:   [...ORG_ADMINS, ...UNDERWRITING, 'claimsManager', 'seniorAdjuster'],
    update:   [...ORG_ADMINS, ...UNDERWRITING, 'claimsManager', 'seniorAdjuster'],
    delete:   ORG_ADMINS,
    validate: [...ORG_ADMINS, ...UNDERWRITING, 'claimsManager'],
    close:    [...ORG_ADMINS, ...UNDERWRITING, 'claimsManager'],
  },
};

// ─── Legacy↔modern role aliasing ─────────────────────────────────
// NOTE: `admin` is NOT aliased to `systemAdmin` here. They are distinct
// roles: `admin` = SaaS God (platform-level), `systemAdmin` = tenant
// admin. Aliasing would let systemAdmins pass any `['admin']` policy,
// defeating the platform.read gate.
const LEGACY_TO_MODERN = {
  org: 'claimsManager',
  clientAdmin: 'seniorAdjuster',
  fileAdmin: 'adjuster',
  fileUser: 'fieldAdjuster',
  patient: 'claimantPortalUser',
  fileUserOnlyDownload: 'externalContractor',
};
const MODERN_TO_LEGACY = Object.fromEntries(
  Object.entries(LEGACY_TO_MODERN).map(([k, v]) => [v, k]),
);

function expandRoles(roles) {
  const set = new Set(roles);
  for (const r of roles) {
    if (LEGACY_TO_MODERN[r]) set.add(LEGACY_TO_MODERN[r]);
    if (MODERN_TO_LEGACY[r]) set.add(MODERN_TO_LEGACY[r]);
  }
  return set;
}

export function hasPermission(role, resource, action) {
  if (!role) return false;
  // SaaS God: `admin` (demo@regure.com) bypasses every check — matches
  // backend behaviour.
  if (role === 'admin') return true;
  const allowed = RBAC?.[resource]?.[action];
  if (!Array.isArray(allowed)) return false;
  return expandRoles(allowed).has(role);
}

// ─── Route → { resource, action } map ────────────────────────────
// Used by RequireRole so /navigation only names the URL, not the
// permission tuple. Public routes (login, signing, guest video) are
// NOT listed here — RequireRole ignores unlisted routes (fail open for
// public routes; still gated at the component/screen level).

// ─── Nav item → permission map ───────────────────────────────────
// Single source of truth for sidebar visibility. Every nav item id used by
// RegureSidebar's NAV_ITEMS structure gets exactly one entry here. The
// derived visibility rule (see utils/rbac.js → getVisibleNavItems):
//
//   • item is `always: true`         → always shown (subject to portalOnly gate below)
//   • item is `portalOnly: true`     → shown only when isPortalUser(role) is true
//                                       AND (always OR hasPermission(role, resource, action))
//   • otherwise (back-office item)   → shown only when !isPortalUser(role)
//                                       AND hasPermission(role, resource, action)
//
// A section header renders iff at least one of its items is visible.
// If you add a nav item to RegureSidebar/NAV_ITEMS, ADD ITS ENTRY HERE.
// Missing entries fail closed (item hidden).
export const NAV_ITEM_PERMISSIONS = {
  // Portal (external users only).
  //
  // Portal nav items are gated by portalRoutes.js `restrictTo` on the
  // backend, not by the shared resource-level RBAC map — the back-office
  // resource lists (policies.read, claims.read etc.) intentionally don't
  // include every portal role (e.g. policyholder isn't in policies.read
  // because the /policies list is back-office only). Reusing those lists
  // to gate sidebar visibility hid "Mes Polices" from policyholders (D-32)
  // even though /me/my-policies works for them. Switch these to
  // `always:true` so visibility follows portal-role membership + the
  // per-item restrictTo below.
  //   my-policies:    all portal roles (PH, broker, claimant)
  //   my-claims:      all portal roles
  //   my-clients:     broker only (see broker-only gate below)
  //   my-commissions: broker only
  //   my-documents:   all portal roles
  //   my-profile:     all portal roles
  'my-policies':    { always: true,                            portalOnly: true },
  'my-claims':      { resource: 'claims',      action: 'read', portalOnly: true },
  'my-clients':     { resource: 'clients',     action: 'read', portalOnly: true },
  'my-commissions': { resource: 'commissions', action: 'read', portalOnly: true },
  'my-documents':   { resource: 'documents',   action: 'read', portalOnly: true },
  'my-profile':     { always: true,                            portalOnly: true },

  // Platform (SaaS admin)
  'platform':       { resource: 'platform',    action: 'read' },
  'tenants':        { resource: 'platform',    action: 'read' },
  'onboard-org':    { resource: 'platform',    action: 'write' },

  // Intelligence
  'dashboard':        { resource: 'dashboard',   action: 'read' },
  'claims-analytics': { resource: 'analytics',   action: 'read' },
  'documents':        { resource: 'documents',   action: 'read' },
  'workflows':        { resource: 'workflows',   action: 'read' },
  'risk-signals':     { resource: 'riskSignals', action: 'read' },

  // Operations
  'clients':     { resource: 'clients',     action: 'read' },
  'claims':      { resource: 'claims',      action: 'read' },
  'policies':    { resource: 'policies',    action: 'read' },
  'offers':      { resource: 'offers',      action: 'read' },
  'invoicing':   { resource: 'invoices',    action: 'read' },
  'commissions': { resource: 'commissions', action: 'read' },

  // Communications
  'chat':  { resource: 'chat',  action: 'read' },
  'video': { resource: 'video', action: 'read' },
  'esign': { resource: 'eSign', action: 'read' },

  // Compliance
  'audit':         { resource: 'audit',         action: 'read' },
  'integrity':     { resource: 'integrity',     action: 'read' },
  'compliance':    { resource: 'compliance',    action: 'read' },
  'residency':     { resource: 'dataResidency', action: 'read' },
  'dsar':          { resource: 'dsar',          action: 'read' },
  'ai-governance': { resource: 'aiGovernance',  action: 'read' },

  // Administration
  'team':         { resource: 'users',        action: 'read' },
  'brokers':      { resource: 'brokers',      action: 'read' },
  'carriers':     { resource: 'riskCarriers', action: 'read' },
  'bordereaux':   { resource: 'bordereaux',   action: 'read' },
  'apikeys':      { resource: 'apiKeys',      action: 'read' },
  'webhooks':     { resource: 'webhooks',     action: 'read' },
  'integrations': { resource: 'orgs',         action: 'update' },
  'settings':     { resource: 'orgs',         action: 'read' },

  // Reinsurance module (feature-flag gated on top of RBAC — see
  // FEATURE_BY_ITEM_ID in RegureSidebar and RequireFeature on routes).
  're-overview':    { resource: 'reinsurance', action: 'read' },
  're-submissions': { resource: 'reinsurance', action: 'read' },
  're-placement':   { resource: 'reinsurance', action: 'read' },
  're-closings':    { resource: 'reinsurance', action: 'read' },
  're-claims':      { resource: 'reinsurance', action: 'validate' },
  're-portfolio':   { resource: 'reinsurance', action: 'read' },
  // Second wave. Reading a treaty, an account, a recovery or a renewal is
  // reinsurance.read like the rest; the counterparty registry carries KYC
  // and sanctions state, so it is gated on `validate` — the same action
  // that guards claims validation — rather than plain read.
  're-treaties':       { resource: 'reinsurance', action: 'read' },
  're-accounting':     { resource: 'reinsurance', action: 'read' },
  're-recoveries':     { resource: 'reinsurance', action: 'read' },
  're-counterparties': { resource: 'reinsurance', action: 'validate' },
  're-renewals':       { resource: 'reinsurance', action: 'read' },
};

export const ROUTE_PERMISSIONS = {
  '/dashboard':          { resource: 'dashboard',    action: 'read' },
  '/claims':             { resource: 'claims',       action: 'read' },
  '/claims/new':         { resource: 'claims',       action: 'create' },
  '/claims/:id':         { resource: 'claims',       action: 'read' },
  '/policies':           { resource: 'policies',     action: 'read' },
  '/policies/:id':       { resource: 'policies',     action: 'read' },
  '/offers':             { resource: 'offers',       action: 'read' },
  '/invoicing':          { resource: 'invoices',     action: 'read' },
  '/commissions':        { resource: 'commissions',  action: 'read' },
  '/brokers':            { resource: 'brokers',      action: 'read' },
  '/risk-carriers':      { resource: 'riskCarriers', action: 'read' },
  '/risk-signals':       { resource: 'riskSignals',  action: 'read' },
  '/workflow-rules':     { resource: 'workflows',    action: 'read' },
  '/bordereaux':         { resource: 'bordereaux',   action: 'read' },
  '/claims-analytics':   { resource: 'analytics',    action: 'read' },
  '/compliance-reports': { resource: 'compliance',   action: 'read' },
  '/data-residency':     { resource: 'dataResidency',action: 'read' },
  '/dsar-requests':      { resource: 'dsar',         action: 'read' },
  '/ai-governance':      { resource: 'aiGovernance', action: 'read' },
  '/api-keys':           { resource: 'apiKeys',      action: 'read' },
  '/webhooks':           { resource: 'webhooks',     action: 'read' },
  '/audit-logs':         { resource: 'audit',        action: 'read' },
  '/audit-integrity':    { resource: 'integrity',    action: 'read' },
  '/clients':            { resource: 'clients',      action: 'read' },
  '/clients/:id':        { resource: 'clients',      action: 'read' },
  '/orgs-admin':         { resource: 'platform',     action: 'read' },
  '/users':              { resource: 'users',        action: 'read' },
  '/platform':           { resource: 'platform',     action: 'read' },
  '/platform/tenants':   { resource: 'platform',     action: 'read' },
  '/platform/tenants/:orgId': { resource: 'platform',action: 'read' },
  '/orgs/onboard':       { resource: 'platform',     action: 'write' },
  '/orgs/:id':           { resource: 'orgs',         action: 'read' },
  '/account-settings':   { resource: 'orgs',         action: 'read' },
  '/integrations':       { resource: 'orgs',         action: 'update' },
  '/website-forms':      { resource: 'orgs',         action: 'update' },
  '/unmatched-documents':{ resource: 'documents',    action: 'read' },
  '/e-signatures':       { resource: 'eSign',        action: 'read' },
  '/e-signatures/:id':   { resource: 'eSign',        action: 'read' },
  '/reinsurance/submissions':{ resource: 'reinsurance', action: 'read' },
  '/reinsurance/deals':      { resource: 'reinsurance', action: 'read' },
  '/reinsurance/deals/:id':  { resource: 'reinsurance', action: 'read' },
  '/reinsurance/placement':  { resource: 'reinsurance', action: 'read' },
  '/reinsurance/deals/:id/placement': { resource: 'reinsurance', action: 'read' },
  '/reinsurance/closings':   { resource: 'reinsurance', action: 'read' },
  '/reinsurance/claims':     { resource: 'reinsurance', action: 'read' },
  '/reinsurance/claims/:id': { resource: 'reinsurance', action: 'read' },
  '/reinsurance/endorsements/:id': { resource: 'reinsurance', action: 'read' },
  '/reinsurance/portfolio':  { resource: 'reinsurance', action: 'read' },
  '/reinsurance/fx-rates':   { resource: 'reinsurance', action: 'read' },
  '/reinsurance/sanctions':  { resource: 'reinsurance', action: 'read' },
  // Second wave. The counterparty registry and the compliance register carry
  // KYC and sanctions state, so both take `validate` rather than `read` —
  // matching their nav entries and their backend routes.
  '/reinsurance':                    { resource: 'reinsurance', action: 'read' },
  '/reinsurance/treaties':           { resource: 'reinsurance', action: 'read' },
  '/reinsurance/treaties/:id':       { resource: 'reinsurance', action: 'read' },
  '/reinsurance/accounting':         { resource: 'reinsurance', action: 'read' },
  '/reinsurance/accounting/:id':     { resource: 'reinsurance', action: 'read' },
  '/reinsurance/recoveries':         { resource: 'reinsurance', action: 'read' },
  '/reinsurance/recoveries/:id':     { resource: 'reinsurance', action: 'read' },
  '/reinsurance/counterparties':     { resource: 'reinsurance', action: 'validate' },
  '/reinsurance/counterparties/:id': { resource: 'reinsurance', action: 'validate' },
  '/reinsurance/compliance':         { resource: 'reinsurance', action: 'validate' },
  '/reinsurance/renewals':           { resource: 'reinsurance', action: 'read' },
  '/reinsurance/renewals/:id':       { resource: 'reinsurance', action: 'read' },
};

export default RBAC;
